import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';

/**
 * MedicalTab Component
 * Displays medical/appointment statistics with Status filtering
 */
const MedicalTab = ({ filters, onFilterChange, onViewDetailed }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const statusCategories = [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    useEffect(() => {
        loadMedicalStats();
    }, [filters]);

    const loadMedicalStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getMedicalStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load medical stats:', err);
            setError(err.message || 'Failed to load medical statistics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full">
                <div className="panel-container" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="loading-skeleton" style={{ width: '40px', height: '40px' }}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadMedicalStats} className="retry-button">
                    Retry
                </button>
            </div>
        );
    }

    const bgColors = {
        pending: '#eab308',
        confirmed: '#3b82f6',
        completed: '#10b981',
        cancelled: '#ef4444'
    };

    return (
        <div className="w-full">
            <div className="flex justify-end mb-6">
                <button
                    onClick={onViewDetailed}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm"
                >
                    View Detailed Analytics <ArrowRight size={16} />
                </button>
            </div>

            {/* Medical Stats Cards */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Total Appointments</h3>
                        <p className="stat-value">
                            {(stats?.totalAppointments || 0).toLocaleString()}
                        </p>
                        <p className="stat-trend trend-up">
                            <span className="mr-1">↑</span> {stats?.appointmentGrowth || 0}% from previous period
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-info)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Active Clinics</h3>
                        <p className="stat-value">
                            {(stats?.activeClinics || 0).toLocaleString()}
                        </p>
                        <p className="stat-trend" style={{ color: 'var(--color-text-secondary)' }}>
                            Registered clinics
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-warning)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Completion Rate</h3>
                        <p className="stat-value">
                            {(stats?.completionRate || 0).toFixed(1)}%
                        </p>
                        <p className="stat-trend" style={{ color: 'var(--color-text-secondary)' }}>
                            Appointments completed
                        </p>
                    </div>
                </div>
            </div>

            {/* Appointment Status Breakdown */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
                <div className="panel-container">
                    <h3 className="panel-title">Appointment Status</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {stats?.appointmentsByStatus && Object.entries(stats.appointmentsByStatus).map(([status, count]) => {
                            const total = stats.totalAppointments || 1;
                            const percentage = ((count / total) * 100).toFixed(1);
                            const category = statusCategories.find(c => c.value === status.toLowerCase());
                            // Map status to badge color for simplicity in inline style fallback
                            const colorMap = {
                                pending: 'var(--color-warning)',
                                confirmed: 'var(--color-info)',
                                completed: 'var(--color-success)',
                                cancelled: 'var(--color-danger)'
                            };

                            return (
                                <div key={status} className="progress-container">
                                    <div className="progress-label-row">
                                        <span>{category?.label || status}</span>
                                        <span style={{ color: 'var(--color-text-secondary)' }}>
                                            {count.toLocaleString()} ({percentage}%)
                                        </span>
                                    </div>
                                    <div className="progress-track">
                                        <div
                                            className="progress-fill"
                                            style={{
                                                width: `${percentage}%`,
                                                background: colorMap[status.toLowerCase()] || 'var(--color-text-secondary)'
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="panel-container">
                    <h3 className="panel-title">Visit Types</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                            borderRadius: 'var(--radius-lg)'
                        }}>
                            <div>
                                <p style={{ fontWeight: 600, color: 'var(--color-text-main)' }}>Outpatient Visits</p>
                                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Regular consultations</p>
                            </div>
                            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-info)' }}>
                                {(stats?.outpatientVisits || 0).toLocaleString()}
                            </p>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            backgroundColor: 'rgba(147, 51, 234, 0.05)',
                            borderRadius: 'var(--radius-lg)'
                        }}>
                            <div>
                                <p style={{ fontWeight: 600, color: 'var(--color-text-main)' }}>Inpatient Visits</p>
                                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Hospital stays</p>
                            </div>
                            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>
                                {(stats?.inpatientVisits || 0).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Appointments */}
            {stats?.recentAppointments && stats.recentAppointments.length > 0 && (
                <div className="panel-container">
                    <h3 className="panel-title">Recent Appointments</h3>
                    <div className="table-container">
                        <table className="dashboard-table">
                            <thead>
                                <tr>
                                    <th>Pet</th>
                                    <th>Owner</th>
                                    <th>Clinic</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.recentAppointments.slice(0, 10).map((appointment, idx) => {
                                    const statusColors = {
                                        pending: 'yellow',
                                        confirmed: 'blue',
                                        completed: 'green',
                                        cancelled: 'red'
                                    };
                                    const badgeColor = statusColors[appointment.status?.toLowerCase()] || 'gray';

                                    return (
                                        <tr key={appointment.id || idx}>
                                            <td style={{ fontWeight: 500 }}>
                                                {appointment.petName || 'N/A'}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {appointment.ownerName || 'N/A'}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {appointment.clinicName || 'N/A'}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {appointment.date ? new Date(appointment.date).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${badgeColor}`}>
                                                    {appointment.status || 'Unknown'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MedicalTab;
